import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Icon } from '@ds/core';
import { useTopbarCompacta } from '@ds/navigation';
import type { AtendimentoEmAndamento } from '@/data/atendimentoEmCurso';
import { dados } from '@/data/porta';
import { pode } from '@/session/sessao';

/**
 * A pílula "Em andamento" da topbar — porte da de `ui_kits/vecchia_app/App.jsx`.
 *
 * Aparece em todas as telas enquanto a pessoa tem uma consulta aberta, e some no
 * próprio editor, onde o cronômetro já está à vista. Relê a cada navegação:
 * consulta encerrada em outra aba não pode deixar a pílula acesa.
 */
export function PilulaDoAtendimento() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const permitido = pode('atendimentos');
    /* No celular vira só o ícone: o nome inteiro segue no aria-label e no title. */
    const compacta = useTopbarCompacta();
    const [aberta, setAberta] = useState<AtendimentoEmAndamento | null>(null);

    useEffect(() => {
        if (!permitido) return;
        let vivo = true;
        dados().atendimentoEmAndamento()
            .then((a) => { if (vivo) setAberta(a); })
            /* Falhar em ler a pílula não apaga consulta nenhuma — ela continua em
               Atendimentos. Um aviso a cada troca de tela seria ruído maior que a
               falta da pílula. */
            .catch(() => { if (vivo) setAberta(null); });
        return () => { vivo = false; };
    }, [pathname, permitido]);

    if (!permitido || !aberta || pathname.startsWith('/app/atendimentos/editor/')) return null;

    return (
        <button
            type="button"
            onClick={() => { void navigate(`/app/atendimentos/editor/${aberta.id}`); }}
            title={`Atendimento em andamento · ${aberta.paciente}`}
            aria-label={`${aberta.pausado ? 'Atendimento pausado' : 'Atendimento em andamento'}: ${aberta.paciente}. Voltar à consulta`}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 36,
                width: compacta ? 36 : undefined, padding: compacta ? 0 : '0 12px', flex: '0 0 auto',
                border: 0, borderRadius: 'var(--radius-control)', background: 'var(--info-soft)',
                color: 'var(--info-strong)', cursor: 'pointer', fontFamily: 'var(--font-ui)',
                fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', transition: 'var(--transition-control)',
            }}
        >
            <Icon name={aberta.pausado ? 'pause' : 'stethoscope'} size={15} />
            {!compacta && (aberta.pausado ? 'Pausado' : 'Em andamento')}
        </button>
    );
}
